import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import FixedVideoBackground from "@/components/homepage/FixedVideoBackground";
import { HttpError, httpClient } from "@/api/http-client";

/**
 * Two-step unsubscribe page.
 *
 * Why not just fetch-on-load?  Several mail clients and corporate
 * security gateways prefetch URLs from inbound emails before the user
 * ever clicks. If the page fired a mutating request on mount, those
 * scanners would silently unsubscribe people. So the flow is:
 *
 *   1. Page loads → GET /subscription-status (read-only, safe to
 *      prefetch) to figure out what we're showing.
 *   2. Show the user their masked email + a Confirm button.
 *   3. User clicks → POST /unsubscribe with the token.
 *
 * Gmail/Apple Mail users who click the native "Unsubscribe" button
 * never see this page — they go through the RFC 8058 one-click POST
 * via the List-Unsubscribe-Post header instead.
 */

type ViewState =
  | { kind: "no-token" }
  | { kind: "loading-status" }
  | {
      kind: "confirm";
      emailMasked: string;
    }
  | { kind: "already" }
  | { kind: "invalid-token" }
  | { kind: "submitting" }
  | { kind: "done"; message: string }
  | { kind: "error"; message: string; httpStatus?: number };

interface StatusResponse {
  status: "confirmed" | "pending" | "unsubscribed" | "bounced";
  emailMasked: string;
}

interface UnsubscribeResponse {
  success: boolean;
  message: string;
}

function describeError(err: unknown): {
  message: string;
  httpStatus?: number;
} {
  if (err instanceof HttpError) {
    const body = err.body as { message?: string } | undefined;
    return {
      message: body?.message || err.message || "Request failed.",
      httpStatus: err.status,
    };
  }
  if (err instanceof Error) {
    return { message: err.message };
  }
  return { message: "Network error." };
}

const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [view, setView] = useState<ViewState>(
    token ? { kind: "loading-status" } : { kind: "no-token" },
  );

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    void (async () => {
      try {
        const res = await httpClient.get<StatusResponse>(
          `/email-subscribers/subscription-status?token=${encodeURIComponent(token)}`,
        );
        if (cancelled) return;
        if (res.status === "unsubscribed") {
          setView({ kind: "already" });
        } else {
          setView({ kind: "confirm", emailMasked: res.emailMasked });
        }
      } catch (err) {
        if (cancelled) return;
        const { message, httpStatus } = describeError(err);
        if (httpStatus === 404) {
          setView({ kind: "invalid-token" });
        } else {
          setView({ kind: "error", message, httpStatus });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleConfirm = async () => {
    if (!token) return;
    setView({ kind: "submitting" });
    try {
      const res = await httpClient.post<UnsubscribeResponse>(
        `/email-subscribers/unsubscribe?token=${encodeURIComponent(token)}`,
        { token },
      );
      setView({
        kind: "done",
        message: res.message || "You've been unsubscribed.",
      });
    } catch (err) {
      const { message, httpStatus } = describeError(err);
      if (httpStatus === 404) {
        setView({ kind: "invalid-token" });
      } else {
        setView({ kind: "error", message, httpStatus });
      }
    }
  };

  return (
    <>
      <FixedVideoBackground />

      <div className="relative min-h-screen py-16">
        <div className="max-w-[1600px] mx-auto px-0 sm:px-12">
          <section className="relative bg-white py-16 sm:py-24 overflow-hidden shadow-2xl rounded-none sm:rounded-3xl">
            <div className="relative z-10 max-w-xl mx-auto px-4 sm:px-6 text-center">
              <p className="text-sm uppercase tracking-widest text-slate-500 font-semibold mb-4">
                Email Preferences
              </p>
              <h1 className="text-3xl sm:text-4xl font-black text-slate-900 leading-tight mb-6">
                Unsubscribe
              </h1>

              {view.kind === "no-token" && (
                <div className="p-5 bg-red-50 border border-red-200 rounded-xl text-red-800 font-semibold">
                  This unsubscribe link is missing a token. Please use the link
                  from your email.
                </div>
              )}

              {view.kind === "loading-status" && (
                <p className="text-slate-600 font-semibold">
                  Looking up your subscription…
                </p>
              )}

              {view.kind === "invalid-token" && (
                <div className="p-5 bg-red-50 border border-red-200 rounded-xl text-red-800 font-semibold">
                  This unsubscribe link is invalid or has expired.
                </div>
              )}

              {view.kind === "confirm" && (
                <div className="space-y-6">
                  <p className="text-slate-700">
                    You're currently subscribed as{" "}
                    <span className="font-bold text-slate-900">
                      {view.emailMasked}
                    </span>
                    . Click below to stop receiving updates.
                  </p>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    className="px-6 py-3 rounded-xl bg-slate-900 text-white font-bold hover:bg-slate-800 transition"
                  >
                    Confirm unsubscribe
                  </button>
                </div>
              )}

              {view.kind === "submitting" && (
                <p className="text-slate-600 font-semibold">
                  Updating your preferences…
                </p>
              )}

              {view.kind === "already" && (
                <div className="p-5 bg-green-50 border border-green-200 rounded-xl text-green-800 font-semibold">
                  You've already been unsubscribed.
                </div>
              )}

              {view.kind === "done" && (
                <div className="p-5 bg-green-50 border border-green-200 rounded-xl text-green-800 font-semibold">
                  {view.message}
                </div>
              )}

              {view.kind === "error" && (
                <div className="p-5 bg-red-50 border border-red-200 rounded-xl text-red-800 font-semibold space-y-2">
                  <p>Something went wrong. Please try again later.</p>
                  <p className="text-xs font-normal text-red-700">
                    {view.httpStatus
                      ? `HTTP ${view.httpStatus} · ${view.message}`
                      : view.message}
                  </p>
                </div>
              )}

              <p className="text-sm text-slate-500 mt-8">
                Changed your mind?{" "}
                <a href="/courses" className="underline hover:text-slate-700">
                  Re-subscribe here
                </a>
                .
              </p>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default UnsubscribePage;
